import { clsx } from 'clsx'
import { ComponentPropsWithoutRef, forwardRef, ReactNode, useId, useState } from 'react'

export type SwitchSize = 'sm' | 'md'

type SwitchType = 'default' | 'slim'
type SwitchPosition = 'on' | 'off'

export interface SwitchProps extends Omit<
    ComponentPropsWithoutRef<'button'>,
    'onChange' | 'defaultChecked' | 'children' | 'type' | 'role'
> {
    checked?: boolean
    defaultChecked?: boolean
    onChange?: (checked: boolean) => void
    size?: SwitchSize
    slim?: boolean
    label?: ReactNode
    supportingText?: ReactNode
    disabled?: boolean
}

const RAIL_SIZE_CLASSES: Record<SwitchType, Record<SwitchSize, string>> = {
    default: {
        sm: 'h-5 w-9',
        md: 'h-6 w-11',
    },
    slim: {
        sm: 'h-4 w-8',
        md: 'h-5 w-10',
    },
}

const RAIL_COLOR_CLASSES: Record<SwitchPosition, string> = {
    on: 'bg-background-bg-brand-solid enabled:hover:bg-background-bg-brand-solid_hover',
    off: 'bg-background-bg-tertiary',
}

const KNOB_SIZE_CLASSES: Record<SwitchSize, string> = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
}

const KNOB_OFFSET_CLASSES: Record<SwitchType, string> = {
    default: 'left-0.5 top-0.5',
    slim: 'left-0 top-0',
}

const KNOB_TRANSLATE_CLASSES: Record<SwitchPosition, Record<SwitchSize, string>> = {
    on: {
        sm: 'translate-x-4',
        md: 'translate-x-5',
    },
    off: {
        sm: 'translate-x-0',
        md: 'translate-x-0',
    },
}

const KNOB_SURFACE_CLASSES: Record<SwitchType, Record<SwitchPosition, string>> = {
    default: {
        on: 'bg-foreground-fg-white shadow-sm',
        off: 'bg-foreground-fg-white shadow-sm',
    },
    slim: {
        on: 'bg-foreground-fg-white shadow-xs border border-border-brand group-enabled:group-hover:border-background-bg-brand-solid_hover',
        off: 'bg-foreground-fg-white shadow-xs border border-border-primary',
    },
}

const WRAPPER_GAP_CLASSES: Record<SwitchSize, string> = {
    sm: 'gap-spacing-md',
    md: 'gap-spacing-lg',
}

const TEXT_SIZE_CLASSES: Record<SwitchSize, string> = {
    sm: 'text-sm',
    md: 'text-md',
}

const TEXT_STACK_GAP_CLASSES: Record<SwitchSize, string> = {
    sm: 'gap-spacing-none',
    md: 'gap-spacing-xxs',
}

const RAIL_ALIGN_CLASSES: Record<SwitchType, string> = {
    default: '',
    slim: 'pt-spacing-xxs',
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
    (
        {
            checked,
            defaultChecked = false,
            onChange,
            size = 'md',
            slim = false,
            label,
            supportingText,
            disabled = false,
            id,
            className,
            onClick,
            ...props
        },
        ref,
    ) => {
        const [internalChecked, setInternalChecked] = useState(defaultChecked)
        const isControlled = checked !== undefined
        const checkedValue = isControlled ? checked : internalChecked

        const generatedId = useId()
        const switchId = id ?? generatedId
        const supportingTextId = `${switchId}-supporting-text`

        const type: SwitchType = slim ? 'slim' : 'default'
        const position: SwitchPosition = checkedValue ? 'on' : 'off'
        const hasText = Boolean(label) || Boolean(supportingText)

        const handleClick: ComponentPropsWithoutRef<'button'>['onClick'] = (event) => {
            onClick?.(event)
            if (disabled || event.defaultPrevented) return

            const nextChecked = !checkedValue
            if (!isControlled) {
                setInternalChecked(nextChecked)
            }
            onChange?.(nextChecked)
        }

        const switchElement = (
            <button
                aria-describedby={supportingText ? supportingTextId : undefined}
                {...props}
                ref={ref}
                id={switchId}
                type="button"
                role="switch"
                aria-checked={checkedValue}
                disabled={disabled}
                onClick={handleClick}
                className={clsx(
                    'group relative inline-flex shrink-0 rounded-radius-full transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    RAIL_SIZE_CLASSES[type][size],
                    RAIL_COLOR_CLASSES[position],
                )}>
                {slim && position === 'off' && (
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 rounded-radius-full border border-border-secondary"
                    />
                )}
                <span
                    aria-hidden="true"
                    className={clsx(
                        'pointer-events-none absolute rounded-radius-full transition-transform',
                        KNOB_SIZE_CLASSES[size],
                        KNOB_OFFSET_CLASSES[type],
                        KNOB_TRANSLATE_CLASSES[position][size],
                        KNOB_SURFACE_CLASSES[type][position],
                    )}
                />
            </button>
        )

        if (!hasText) {
            return <div className={clsx('inline-flex', className)}>{switchElement}</div>
        }

        return (
            <div
                className={clsx(
                    'inline-flex items-start font-body',
                    WRAPPER_GAP_CLASSES[size],
                    className,
                )}>
                <div className={clsx('flex shrink-0', RAIL_ALIGN_CLASSES[type])}>
                    {switchElement}
                </div>
                <div className={clsx('flex flex-col', TEXT_STACK_GAP_CLASSES[size])}>
                    {label && (
                        <label
                            htmlFor={switchId}
                            className={clsx(
                                'font-medium text-text-secondary',
                                TEXT_SIZE_CLASSES[size],
                                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                            )}>
                            {label}
                        </label>
                    )}
                    {supportingText && (
                        <div
                            id={supportingTextId}
                            className={clsx(
                                'font-regular text-text-tertiary',
                                TEXT_SIZE_CLASSES[size],
                            )}>
                            {supportingText}
                        </div>
                    )}
                </div>
            </div>
        )
    },
)

Switch.displayName = 'Switch'
